/**
 * The conditions' lanes: their groups, check history, bars and verdicts, and a lane per
 * part (lint, the type check, coverage) under a repo-checks condition.
 *
 * @since 1.0.0
 */
import { Match, Option, Schema } from "effect";
import { findConditionKind, findConnector } from "../catalog.ts";
import type { ConditionSpec, ConditionState, Evidence } from "../model.ts";
import type { Bar, ConditionAttempt, ConditionRow, GroupRow, PartRow, ViewState } from "../RunView.ts";
import { duration, optional, shortSubject } from "./format.ts";
import type { EventOf, Timeline } from "./timeline.ts";
import { storyKey } from "./timeline.ts";

/**
 * A verdict as a view state: a pending check is waiting.
 *
 * @since 1.0.0
 */
export const VERDICT: Readonly<Record<ConditionState, ViewState>> = {
  met: "met",
  unmet: "unmet",
  pending: "waiting",
};

/** The repo's own checks: the one condition kind whose checks report parts. */
const CHECKS_KIND = "workspace.checks";

/** The run page's names for catalog kinds, where they differ from the catalog's title. */
const PAGE_TITLES: ReadonlyMap<string, string> = new Map([[CHECKS_KIND, "Tests, types & lint"]]);

/**
 * The spec's title, else the run page's name for its kind, else its catalog kind's title,
 * else its id.
 *
 * @since 1.0.0
 */
export const conditionTitle = (spec: ConditionSpec): string =>
  spec.title ??
    PAGE_TITLES.get(spec.kind) ??
    findConditionKind(spec.kind, spec.connector).pipe(
      Option.map((k) => k.title),
      Option.getOrElse(() => spec.id),
    );

const PART_FIELD = "check.";

const PartValue = Schema.Struct({ pass: Schema.Boolean, ms: Schema.Number, reused: Schema.optional(Schema.Unknown) });
const decodePart = Schema.decodeUnknownOption(PartValue);

/**
 * One part of a repo check, as its `check.<part>` evidence reports it.
 *
 * @since 1.0.0
 */
export interface PartResult {
  readonly part: string;
  readonly pass: boolean;
  readonly ms: number;
  /** The part's result was reused from an earlier check rather than run. */
  readonly reused: boolean;
}

/**
 * `check.<part>` results of one check, in order; other/undecodable evidence skipped.
 *
 * @since 1.0.0
 */
export const partsOf = (evidence: ReadonlyArray<Evidence>): ReadonlyArray<PartResult> =>
  evidence.flatMap((e): ReadonlyArray<PartResult> =>
    e.field.startsWith(PART_FIELD)
      ? Option.match(decodePart(e.value), {
        onNone: () => [],
        onSome: (v) => [{ part: e.field.slice(PART_FIELD.length), pass: v.pass, ms: v.ms, reused: "reused" in v }],
      })
      : []
  );

/**
 * A part's row key: `part:<conditionId>:<part>`.
 *
 * @since 1.0.0
 */
export const partKey = (conditionId: string, part: string): string => `part:${conditionId}:${part}`;

const partNames = (parts: ReadonlyArray<PartResult>): ReadonlyArray<string> => [...new Set(parts.map((p) => p.part))];

/**
 * The part rows a check of this condition belongs to: its evidence's parts for a
 * repo-checks condition, else none.
 *
 * @since 1.0.0
 */
export const checkPartKeys = (spec: ConditionSpec, evidence: ReadonlyArray<Evidence>): ReadonlyArray<string> =>
  spec.kind === CHECKS_KIND ? partNames(partsOf(evidence)).map((p) => partKey(spec.id, p)) : [];

const connectorTitle = (id: string): string =>
  findConnector(id).pipe(
    Option.map((c) => c.title),
    Option.getOrElse(() => id),
  );

interface Group {
  readonly key: string;
  readonly label: string;
}

const groupOf = (spec: ConditionSpec): Group =>
  Match.value(spec.by).pipe(
    Match.when("runtime", (): Group => ({ key: "group:runtime", label: "Runtime" })),
    Match.when("reviewer", (): Group => ({ key: "group:reviewer", label: "Fresh reviewer" })),
    Match.when("person", (): Group => ({ key: "group:person", label: "A person" })),
    Match.when("connector", (): Group => {
      const id = spec.connector ?? "connector";
      return { key: `group:connector:${id}`, label: connectorTitle(id) };
    }),
    Match.exhaustive,
  );

type ConditionEvent = EventOf<"condition.started"> | EventOf<"condition.checked">;

const attemptOf = (tl: Timeline, e: EventOf<"condition.checked">): ConditionAttempt => ({
  subject: e.result.subject,
  state: e.result.state,
  atMs: tl.ms(e),
  ms: e.result.ms,
  ...optional("costUsd", e.result.costUsd),
  findings: e.result.findings ?? [],
  ...optional("detail", e.result.detail),
  ...optional("log", e.result.log),
  reused: e.result.reused ?? false,
  evidence: e.result.evidence,
});

/** A finished check and the span its bar covers. */
interface Span {
  readonly check: EventOf<"condition.checked">;
  readonly startMs: number;
  readonly endMs: number;
}

/**
 * A condition's spans: a start opens one, the next check of the same subject closes it;
 * a check with no open span covers its own duration. Spans still open are live bars.
 */
const spansOf = (
  tl: Timeline,
  events: ReadonlyArray<ConditionEvent>,
): { readonly closed: ReadonlyArray<Span>; readonly going: ReadonlyArray<Bar> } => {
  const open = new Map<string, number>();
  const closed = events.flatMap((e): ReadonlyArray<Span> => {
    if (e.kind === "condition.started") {
      open.set(e.subject, tl.ms(e));
      return [];
    }
    const endMs = tl.ms(e);
    const startMs = open.get(e.result.subject) ?? endMs - e.result.ms;
    open.delete(e.result.subject);
    return [{ check: e, startMs, endMs }];
  });
  const going = [...open].map(([subject, startMs]): Bar => ({
    startMs,
    state: "live",
    label: shortSubject(subject),
  }));
  return { closed, going };
};

/** A condition's bars: one per span with its check's verdict, then the live ones, by start. */
const barsOf = (closed: ReadonlyArray<Span>, going: ReadonlyArray<Bar>): ReadonlyArray<Bar> =>
  [
    ...closed.map(({ check, endMs, startMs }): Bar => ({
      startMs,
      endMs,
      state: VERDICT[check.result.state],
      label: shortSubject(check.result.subject),
      storyKey: storyKey(check),
    })),
    ...going,
  ].sort((a, b) => a.startMs - b.startMs);

interface PartRun {
  readonly result: PartResult;
  readonly bar: Bar;
}

/**
 * One check's parts laid out within its span. When their durations fit the span they ran
 * one at a time (they share a host permit), each starting where the earlier ones ended;
 * otherwise each starts at the span's start. No bar outruns the span.
 */
const partRunsOf = ({ check, endMs, startMs }: Span): ReadonlyArray<PartRun> => {
  const parts = partsOf(check.result.evidence);
  const msBefore = (i: number) => parts.slice(0, i).reduce((sum, p) => sum + p.ms, 0);
  const oneAtATime = msBefore(parts.length) <= endMs - startMs;
  return parts.map((result, i): PartRun => {
    const partStart = startMs + (oneAtATime ? msBefore(i) : 0);
    return {
      result,
      bar: {
        startMs: partStart,
        endMs: Math.min(partStart + result.ms, endMs),
        state: result.pass ? "met" : "unmet",
        label: shortSubject(check.result.subject),
        storyKey: storyKey(check),
      },
    };
  });
};

/**
 * A repo-checks condition's part rows, one per part name in first-seen order: a bar per
 * check that ran it, then the condition's live bars; live while the condition is, else the
 * latest run's verdict.
 */
const partRows = (
  spec: ConditionSpec,
  closed: ReadonlyArray<Span>,
  going: ReadonlyArray<Bar>,
  fixAgents: ReadonlyArray<string>,
): ReadonlyArray<PartRow> => {
  const runs = closed.flatMap(partRunsOf);
  return partNames(runs.map((r) => r.result)).map((part): PartRow => {
    const mine = runs.filter((r) => r.result.part === part);
    const latest = mine[mine.length - 1].result;
    return {
      kind: "part",
      key: partKey(spec.id, part),
      label: part,
      anchor: `${spec.id}-${part}`,
      conditionId: spec.id,
      part,
      state: going.length > 0 ? "live" : latest.pass ? "met" : "unmet",
      bars: [...mine.map((r) => r.bar), ...going],
      ticks: [],
      related: [`condition:${spec.id}`, ...fixAgents.map((a) => `agent:${a}`)],
      meta: latest.reused ? "reused" : duration(latest.ms),
    };
  });
};

const conditionRow = (
  tl: Timeline,
  spec: ConditionSpec,
  fixAgents: ReadonlyArray<string>,
): ReadonlyArray<ConditionRow | PartRow> => {
  const events = tl.events.filter((e): e is ConditionEvent =>
    (e.kind === "condition.started" || e.kind === "condition.checked") && e.conditionId === spec.id
  );
  const { closed, going } = spansOf(tl, events);
  const history = closed.map((s) => attemptOf(tl, s.check));
  const bars = barsOf(closed, going);
  const latest = history.at(-1);
  const state: ViewState = going.length > 0
    ? "live"
    : latest === undefined
    ? "pending"
    : VERDICT[latest.state];
  const last = events.at(-1);
  const head = last === undefined
    ? undefined
    : shortSubject(last.kind === "condition.started" ? last.subject : last.result.subject);
  const parts = spec.kind === CHECKS_KIND ? partRows(spec, closed, going, fixAgents) : [];
  const title = conditionTitle(spec);
  return [
    {
      kind: "condition",
      key: `condition:${spec.id}`,
      label: title,
      anchor: spec.id,
      state,
      bars,
      ticks: [],
      related: [...fixAgents.map((a) => `agent:${a}`), ...parts.map((p) => p.key)],
      ...optional("meta", head),
      ...optional("head", head),
      detail: {
        conditionId: spec.id,
        kind: spec.kind,
        title,
        decider: spec.by,
        ...optional("connector", spec.connector),
        history,
        fixAgents,
      },
    },
    ...parts,
  ];
};

/**
 * The conditions' group and lane rows: runtime, fresh-reviewer and person-decided groups,
 * then one per connector; conditions in definition order within each, empty groups
 * skipped; a repo-checks condition's part rows right after it.
 *
 * @since 1.0.0
 */
export const conditionRows = (
  tl: Timeline,
  conditions: ReadonlyArray<ConditionSpec>,
  fixAgents: (conditionId: string) => ReadonlyArray<string>,
): ReadonlyArray<GroupRow | ConditionRow | PartRow> => {
  const grouped = conditions.map((spec) => ({ spec, group: groupOf(spec) }));
  const keys = [
    "group:runtime",
    "group:reviewer",
    "group:person",
    ...grouped.filter((g) => g.spec.by === "connector").map((g) => g.group.key),
  ];
  return keys
    .filter((key, i) => keys.indexOf(key) === i)
    .flatMap((key): ReadonlyArray<GroupRow | ConditionRow | PartRow> => {
      const members = grouped.filter((g) => g.group.key === key);
      return members.length === 0 ? [] : [
        { kind: "group", key, label: members[0].group.label },
        ...members.flatMap((m) => conditionRow(tl, m.spec, fixAgents(m.spec.id))),
      ];
    });
};
